import React, {
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useState,
} from "react";

import { round } from "lodash";
import { makeAutoObservable, runInAction } from "mobx";

import client from "@/client";
import { graphql } from "@/gql";
import { CreateRecipeInput, UpdateRecipeInput } from "@/gql/graphql";

import { Ingredient } from "./Ingredient";
import { Instruction } from "./Instruction";
import { Step } from "./Step";

type IngredientMacros = {
  calories: number;
  carbohydrates: number;
  proteins: number;
  fats: number;
  totalCarbsGrams: number;
  totalProteinGrams: number;
  totalFatGrams: number;
};

class RecipeStore {
  recipeId: string | undefined = undefined;
  name = "";
  preparationTime = 0;
  portionsPerRecipe = 0;
  description = "";
  ingredients: Ingredient[] = [];
  selectedIngredient: Ingredient | undefined = undefined;
  step = new Step();
  instructions: Instruction[] = [];
  isEditingForm = false;
  isSubmittingForm = false;

  constructor() {
    makeAutoObservable(this);
  }

  // Getters
  get hasName(): boolean {
    return this.name !== "";
  }

  get hasPreparationTime(): boolean {
    return this.preparationTime !== 0;
  }

  get hasPortionsPerRecipe(): boolean {
    return this.portionsPerRecipe !== 0;
  }

  get hasDescription(): boolean {
    return this.description !== "";
  }

  get hasIngredients(): boolean {
    return this.ingredients.length !== 0;
  }

  get isCompleted(): boolean {
    return (
      this.hasName &&
      this.hasPreparationTime &&
      this.hasPortionsPerRecipe &&
      this.hasDescription &&
      this.hasIngredients
    );
  }

  get hasEmptyInstruction(): boolean {
    return this.instructions.some((item) => item.text === "");
  }

  get totalMacros(): IngredientMacros {
    let _calories = 0;
    let _carbohydrates = 0;
    let _proteins = 0;
    let _fats = 0;
    let _weight = 0;
    let _totalCarbsGrams = 0;
    let _totalProteinGrams = 0;
    let _totalFatGrams = 0;

    this.ingredients.forEach((ingredient) => {
      // Sum them up kcals and grams
      const { foodWeight, calories, carbohydrates, proteins, fats } =
        ingredient.macros;
      _weight += foodWeight;
      _calories += calories;
      _carbohydrates += carbohydrates;
      _proteins += proteins;
      _fats += fats;
    });

    if (this.selectedIngredient) {
      const { calories, carbohydrates, proteins, fats, foodWeight } =
        this.selectedIngredient.macros;
      // Sum them up kcals and grams
      _weight += foodWeight;
      _calories += calories;
      _carbohydrates += carbohydrates;
      _proteins += proteins;
      _fats += fats;
    }

    // Calculate the macros' percentage
    _totalCarbsGrams = round(_carbohydrates, 3);
    _totalProteinGrams = round(_proteins, 3);
    _totalFatGrams = round(_fats, 3);

    _carbohydrates = _carbohydrates / (_weight || 1);
    _proteins = _proteins / (_weight || 1);
    _fats = _fats / (_weight || 1);

    return {
      calories: _calories,
      carbohydrates: _carbohydrates,
      proteins: _proteins,
      fats: _fats,
      totalCarbsGrams: _totalCarbsGrams,
      totalProteinGrams: _totalProteinGrams,
      totalFatGrams: _totalFatGrams,
    };
  }

  // Methods
  setName = (name: string) => {
    this.name = name;
  };

  setPreparationTime = (time: number) => {
    this.preparationTime = time;
  };

  setPortionsPerRecipe = (portion: number) => {
    this.portionsPerRecipe = portion;
  };

  setDescription = (desciprtion: string) => {
    this.description = desciprtion;
  };

  setSelectedIngredient = (ingredient: Ingredient) => {
    this.selectedIngredient = ingredient;
  };

  loadRecipe = async (id: string): Promise<void> => {
    this.editingForm(true);

    const query = await client.query({
      query: recipeQuery,
      variables: { id },
      fetchPolicy: "network-only",
    });

    const recipe = query.data.recipe;

    runInAction(() => {
      if (!recipe) return;

      this.recipeId = recipe.id;
      this.name = recipe.name;
      this.preparationTime = recipe.preparationMinutes;
      this.portionsPerRecipe = recipe.numberOfPortions;
      this.description = recipe.description;
      this.ingredients =
        recipe.ingredients?.map((item) => {
          const { id, amount, food, foodPortion } = item;
          return new Ingredient(id, amount, food, foodPortion);
        }) ?? [];
      this.instructions =
        recipe.instructions?.map(
          (instruction) => new Instruction(instruction)
        ) ?? [];
    });
  };

  addIngredient = () => {
    const ingredient = new Ingredient(
      this.selectedIngredient?.id,
      this.selectedIngredient?.quantity,
      this.selectedIngredient?.ingredient,
      this.selectedIngredient?.portionSize
    );
    this.ingredients.push(ingredient);
  };

  removeIngredient = (index: number) => {
    this.ingredients.splice(index, 1);
  };

  updateIngredient = (ingredient: Ingredient) => {
    const updatedList = this.ingredients.map((item) => {
      if (item.portionSize?.id !== ingredient.portionSize?.id) return item;

      return ingredient;
    });

    this.ingredients = updatedList;
  };

  moveInstructionPosition = (from: number, to: number) => {
    const [instructionFrom, instructionTo] = [
      this.instructions[from],
      this.instructions[to],
    ];
    this.instructions[from] = instructionTo;
    this.instructions[to] = instructionFrom;
  };

  addInstruction = (instruction: Instruction) => {
    this.instructions.push(new Instruction(instruction.text));
  };

  removeInstruction = (index: number) => {
    this.instructions.splice(index, 1);
  };

  updateInstruction = (instruction: Instruction) => {
    const updatedList = this.instructions.map((item) => {
      if (item.id !== instruction.id) return item;

      return instruction;
    });

    this.instructions = updatedList;
  };

  editingForm = (isEdit: boolean): void => {
    this.isEditingForm = isEdit;
  };

  submittingForm = (isSummitting: boolean): void => {
    this.isSubmittingForm = isSummitting;
  };

  toCreateRecipeInput = (): CreateRecipeInput => {
    const input: CreateRecipeInput = {
      attributes: {
        name: this.name,
        preparationMinutes: this.preparationTime,
        numberOfPortions: this.portionsPerRecipe,
        description: this.description,
        ingredientsAttributes: this.ingredients.map((ingredient) => ({
          foodId: ingredient.ingredient?.id,
          foodPortionId: ingredient.portionSize?.id,
          amount: ingredient.quantity,
          _destroy: false,
        })),
        instructions: this.instructions.map((instruction) => instruction.text),
      },
    };

    return input;
  };

  toUpdateRecipeInput = (): UpdateRecipeInput => {
    const input: UpdateRecipeInput = {
      id: this.recipeId ?? "",
      patch: {
        name: this.name,
        preparationMinutes: this.preparationTime,
        numberOfPortions: this.portionsPerRecipe,
        description: this.description,
        ingredientsAttributes: this.ingredients.map((i) => ({
          id: i.id,
          foodId: i.ingredient?.id,
          foodPortionId: i.portionSize?.id,
          amount: i.quantity,
          _destroy: false,
        })),
        instructions: this.instructions.map((i) => i.text),
      },
    };

    return input;
  };

  removeSelectedIngredient = (): void => {
    this.selectedIngredient = undefined;
  };
}

export const RecipeContext = createContext<RecipeStore | undefined>(undefined);

export const RecipeProvider: React.FC<{
  children: ReactElement | ReactNode;
}> = ({ children }) => {
  const [store] = useState(new RecipeStore());

  return (
    <RecipeContext.Provider value={store}>{children}</RecipeContext.Provider>
  );
};

export const useRecipe = (): RecipeStore => {
  const context = useContext(RecipeContext);

  if (!context) throw new Error("useRecipe must be used inside RecipeProvider");

  return context;
};

// gql
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const recipeItemFragment = graphql(`
  fragment RecipeItem on Recipe {
    id
    carbohydrateGrams
    fatGrams
    proteinGrams
    name
    description
    numberOfPortions
    preparationMinutes
    ingredients {
      ...IngredientItem
    }
    instructions
  }
`);

export const createRecipeMutation = graphql(`
  mutation CreateRecipe($input: CreateRecipeInput!) {
    createRecipe(input: $input) {
      recipe {
        ...RecipeItem
      }
    }
  }
`);

export const updateRecipeMutation = graphql(`
  mutation UpdateRecipe($input: UpdateRecipeInput!) {
    updateRecipe(input: $input) {
      recipe {
        ...RecipeItem
      }
    }
  }
`);

const recipeQuery = graphql(`
  query recipeQuery($id: ID!) {
    recipe(id: $id) {
      ...RecipeItem
    }
  }
`);
